import { CSSProperties, forwardRef, ReactNode } from 'react';

import { Dialog as RadixDialog } from '@radix-ui/themes';
import { Flex, Heading, Text } from '@/components/ui';
import { ScrollArea } from './ScrollArea';
import clsx from 'clsx';

export type DialogSize =
  | 'small'
  | 'medium'
  | 'large'
  | 'xlarge'
  | 'none'
  | 'expand';

interface DialogRootProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: ReactNode;
}

interface DialogContentProps {
  size?: DialogSize;
  className?: string;
  children: ReactNode;
  onOpenAutoFocus?: (event: Event) => void;
  onCloseAutoFocus?: (event: Event) => void;
  onEscapeKeyDown?: (event: KeyboardEvent) => void;
  onPointerDownOutside?: (event: CustomEvent) => void;
  onInteractOutside?: (event: Event) => void;
  style?: CSSProperties;
  /**
   * Whether to disable the ScrollArea inside content. Use only when you need custom scrolling.
   */
  disableScrollArea?: boolean;
  /**
   * Whether the dialog should be fullscreen (take up the entire viewport).
   */
  fullscreen?: boolean;
}

interface DialogHeaderProps {
  title?: ReactNode;
  subtitle?: ReactNode;
  startSlot?: ReactNode;
  endSlot?: ReactNode;
  className?: string;
  children?: ReactNode;
}

interface DialogTitleProps {
  className?: string;
  children: ReactNode;
}

interface DialogDescriptionProps {
  className?: string;
  children: ReactNode;
}

interface DialogBodyProps {
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
  /**
   * Whether to disable the ScrollArea. Use only when you need custom scrolling.
   */
  disableScrollArea?: boolean;
}

interface DialogFooterProps {
  className?: string;
  children: ReactNode;
  align?: 'left' | 'center' | 'right' | 'between';
}

interface DialogCloseProps {
  className?: string;
  children: ReactNode;
}

interface DialogTriggerProps {
  className?: string;
  children: ReactNode;
  // asChild?: boolean;
}

const sizeMapping = {
  small: '1',
  medium: '2',
  large: '3',
  xlarge: '4',
} as const;

const DialogRoot = ({ open, onOpenChange, children }: DialogRootProps) => {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      {children}
    </RadixDialog.Root>
  );
};

const DialogTrigger = forwardRef<HTMLButtonElement, DialogTriggerProps>(
  ({ className, children, ...props }, ref) => {
    const computedClassName = clsx('dialog__trigger', className);

    return (
      <RadixDialog.Trigger
        ref={ref}
        // asChild={asChild}
        className={computedClassName}
        {...props}
      >
        {children}
      </RadixDialog.Trigger>
    );
  }
);
DialogTrigger.displayName = 'DialogTrigger';

const DialogContent = forwardRef<HTMLDivElement, DialogContentProps>(
  (
    {
      size = 'medium',
      className,
      children,
      onOpenAutoFocus,
      onCloseAutoFocus,
      onEscapeKeyDown,
      onPointerDownOutside,
      onInteractOutside,
      style,
      disableScrollArea = false,
      fullscreen = false,
      ...props
    },
    ref
  ) => {
    void disableScrollArea;
    const radixSize =
      size in sizeMapping
        ? sizeMapping[size as keyof typeof sizeMapping]
        : undefined;
    const computedClassName = clsx(
      'dialog__content',
      `dialog__content--size-${size}`,
      fullscreen && 'dialog__content--fullscreen',
      className
    );

    return (
      <RadixDialog.Content
        ref={ref}
        size={fullscreen ? undefined : radixSize}
        className={computedClassName}
        onOpenAutoFocus={onOpenAutoFocus}
        onCloseAutoFocus={onCloseAutoFocus}
        onEscapeKeyDown={onEscapeKeyDown}
        onPointerDownOutside={onPointerDownOutside}
        onInteractOutside={onInteractOutside}
        style={style}
        {...props}
      >
        {children}
      </RadixDialog.Content>
    );
  }
);
DialogContent.displayName = 'DialogContent';

const DialogTitle = forwardRef<HTMLHeadingElement, DialogTitleProps>(
  ({ className, children, ...props }, ref) => {
    const computedClassName = clsx('dialog__title', className);

    return (
      <RadixDialog.Title ref={ref} className={computedClassName} {...props}>
        {children}
      </RadixDialog.Title>
    );
  }
);
DialogTitle.displayName = 'DialogTitle';

const DialogDescription = forwardRef<
  HTMLParagraphElement,
  DialogDescriptionProps
>(({ className, children, ...props }, ref) => {
  const computedClassName = clsx('dialog__description', className);

  return (
    <RadixDialog.Description ref={ref} className={computedClassName} {...props}>
      {children}
    </RadixDialog.Description>
  );
});
DialogDescription.displayName = 'DialogDescription';

const DialogHeader = forwardRef<HTMLDivElement, DialogHeaderProps>(
  (
    { title, subtitle, startSlot, endSlot, className, children, ...props },
    ref
  ) => {
    const computedClassName = clsx('dialog__header', className);

    // If children are provided, use the simple version for backward compatibility
    if (children) {
      return (
        <div ref={ref} className={computedClassName} {...props}>
          {children}
        </div>
      );
    }

    // Use the structured version with title, subtitle, slots
    return (
      <Flex
        ref={ref}
        className={computedClassName}
        justify="between"
        align="center"
        {...props}
      >
        <Flex align="center" gap="3" className="dialog__header__content">
          {startSlot && (
            <div className="dialog__header__start-slot">{startSlot}</div>
          )}
          <Flex direction="column" gap="1">
            {title && (
              <Heading
                as="h3"
                size="xsmall"
                weight="medium"
                className="dialog__header__title"
              >
                {title}
              </Heading>
            )}
            {subtitle && (
              <Text
                size="2xsmall"
                color="gray"
                className="dialog__header__subtitle"
              >
                {subtitle}
              </Text>
            )}
          </Flex>
        </Flex>
        {endSlot && <div className="dialog__header__end-slot">{endSlot}</div>}
      </Flex>
    );
  }
);
DialogHeader.displayName = 'DialogHeader';

const DialogBody = forwardRef<HTMLDivElement, DialogBodyProps>(
  (
    { className, style, children, disableScrollArea = false, ...props },
    ref
  ) => {
    const computedClassName = clsx('dialog__body', className);

    return (
      <Flex
        direction="column"
        ref={ref}
        className={computedClassName}
        style={style}
        {...props}
      >
        {disableScrollArea ? (
          children
        ) : (
          <ScrollArea className="dialog__body__scroll-area">
            <Flex direction={'column'} className="dialog__body__content">
              {children}
            </Flex>
          </ScrollArea>
        )}
      </Flex>
    );
  }
);
DialogBody.displayName = 'DialogBody';

const DialogFooter = forwardRef<HTMLDivElement, DialogFooterProps>(
  ({ className, children, align = 'right', ...props }, ref) => {
    const computedClassName = clsx(
      'dialog__footer',
      `dialog__footer--align-${align}`,
      className
    );

    return (
      <Flex ref={ref} className={computedClassName} gap="3" {...props}>
        {children}
      </Flex>
    );
  }
);
DialogFooter.displayName = 'DialogFooter';

const DialogClose = forwardRef<HTMLButtonElement, DialogCloseProps>(
  ({ className, children, ...props }, ref) => {
    const computedClassName = clsx('dialog__close', className);

    return (
      <RadixDialog.Close ref={ref} className={computedClassName} {...props}>
        {children}
      </RadixDialog.Close>
    );
  }
);
DialogClose.displayName = 'DialogClose';

// Individual component exports
export {
  DialogRoot,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  DialogClose,
};

export type {
  DialogRootProps,
  DialogContentProps,
  DialogHeaderProps,
  DialogTitleProps,
  DialogDescriptionProps,
  DialogBodyProps,
  DialogFooterProps,
  DialogCloseProps,
  DialogTriggerProps,
};
